import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PackageListsService, PackageListOutModel, DeleteEmptyPackageResponse, AddSparePartToPackageResponse, UniquePackageModel } from '../../../services/packageLists/package-lists.service';
import { OrdersService } from '../../../services/orders/orders.service';
import { LabelsService, SparePartLabelInfo } from '../../../services/labels/labels.service';
import { LabelPrintAuthorizationService } from '../../../services/labels/label-print-authorization.service';

@Component({
  selector: 'app-packages-management',
  templateUrl: './packages-management.component.html',
  styleUrls: ['./packages-management.component.css']
})
export class PackagesManagementComponent implements OnInit {
  packages: any[] = [];
  loading = false;
  loadingProgress = 0;
  loadingInterval: any;

  // Loading específico para detalles
  loadingDetails = false;

  selectedPackage: any = null;
  showDeleteConfirmation = false;
  packageToDelete: string = '';
  availableSpareParts: any[] = [];
  showAddSparePartModal = false;
  loadingAvailableParts = false;
  searchTerm: string = '';

  // Estado de impresión de etiquetas
  packageLabelsPrinted: { [packageNumber: string]: boolean } = {};
  sparePartLabelsPrinted: { [packageNumber: string]: boolean } = {};

  private getOrderSendYesOrNo(order: any): string | boolean | undefined {
    return order?.sendYesOrNo ?? order?.SendYesOrNo;
  }

  private isOrderBlockedForLabelPrint(order: any): boolean {
    const sendYesOrNo = this.getOrderSendYesOrNo(order);
    return sendYesOrNo === false || sendYesOrNo === 'false';
  }

  constructor(
    private packageListsService: PackageListsService,
    private ordersService: OrdersService,
    private labelsService: LabelsService,
    private labelPrintAuth: LabelPrintAuthorizationService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadPackages();
  }

  goBack(): void {
    this.router.navigate(['/admin/package-management']);
  }

  startLoadingSimulation() {
    this.loading = true;
    this.loadingProgress = 0;

    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
    }

    // Simular carga hasta 60 segundos (aprox 1.6% por segundo)
    // Incrementamos cada 100ms
    this.loadingInterval = setInterval(() => {
      if (this.loadingProgress < 95) {
        // Incremento pequeño para que sea suave
        // 60 segundos = 60000ms. Si actualizamos cada 100ms son 600 pasos.
        // 100% / 600 pasos = 0.166% por paso
        this.loadingProgress += 0.2;
        // Redondear a 1 decimal
        this.loadingProgress = Math.round(this.loadingProgress * 10) / 10;
      }
    }, 100);
  }

  stopLoadingSimulation() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
    }
    this.loadingProgress = 100;
    // Pequeña pausa para ver el 100% antes de ocultar
    setTimeout(() => {
      this.loading = false;
      this.loadingProgress = 0;
    }, 500);
  }

  get filteredPackages(): any[] {
    if (!this.searchTerm.trim()) {
      return this.packages;
    }
    const term = this.searchTerm.toLowerCase().trim();
    return this.packages.filter(pkg =>
      pkg.packageNumber.toLowerCase().includes(term) ||
      (pkg.name && pkg.name.toLowerCase().includes(term))
    );
  }

  loadPackages(): void {
    this.startLoadingSimulation();
    // Usar getUniquePackages para obtener los paquetes físicos existentes
    this.packageListsService.getUniquePackages().subscribe({
      next: (response: UniquePackageModel[]) => {
        // Mapear la respuesta al formato esperado por el componente
        this.packages = response.map(pkg => ({
          packageNumber: pkg.packageNumber,
          name: pkg.packageListName || pkg.packageNumber,
          type: pkg.listType || 'empaquetado',
          status: 'active', // Por defecto activo ya que getUniquePackages filtra activos principalmente
          createdAt: pkg.loadedToTruckAt ?? pkg.createdAt ?? null,
          loadedToTruckAt: pkg.loadedToTruckAt ?? null,
          totalItems: pkg.sparePartsCount,
          packageListId: pkg.packageListId,
          items: [], // Inicialmente vacío
          orderNumber: pkg.orderNumber // Mapear el orderNumber del resumen para usarlo de fallback
        }));
        this.stopLoadingSimulation();
      },
      error: (error) => {
        console.error('Error cargando paquetes:', error);
        this.stopLoadingSimulation();
      }
    });
  }

  selectPackage(packageNumber: string): void {
    this.selectedPackage = this.packages.find(pkg => pkg.packageNumber === packageNumber);

    // Cargar detalles (repuestos) del paquete si no están cargados
    if (this.selectedPackage && (!this.selectedPackage.spareParts || this.selectedPackage.spareParts.length === 0)) {
      this.loadingDetails = true; // Usar loading local
      this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
        next: (items) => {
          this.selectedPackage.items = items;
          this.selectedPackage.spareParts = items;
          this.loadingDetails = false;
        },
        error: (error) => {
          console.error('Error cargando detalles del paquete:', error);
          this.loadingDetails = false;
        }
      });
    } else {
      // Si ya están cargados, asegurar que loadingDetails sea false
      this.loadingDetails = false;
    }

    // Limpiar repuestos disponibles al cambiar de paquete
    this.availableSpareParts = [];
  }

  confirmDeletePackage(packageName: string): void {
    this.packageToDelete = packageName;
    this.showDeleteConfirmation = true;
  }

  deletePackage(): void {
    if (!this.packageToDelete) return;

    this.startLoadingSimulation();
    // Buscar el paquete por packageNumber
    const packageToDelete = this.packages.find(pkg => pkg.packageNumber === this.packageToDelete);
    if (!packageToDelete) {
      alert('Paquete no encontrado');
      this.stopLoadingSimulation();
      return;
    }

    // Usar deletePackage que restaura los items
    this.packageListsService.deletePackage(packageToDelete.packageNumber).subscribe({
      next: (response: any) => {
        alert(response.Message || response.message || 'Paquete eliminado exitosamente');
        this.showDeleteConfirmation = false;
        this.packageToDelete = '';
        this.selectedPackage = null;
        this.availableSpareParts = []; // Limpiar repuestos disponibles
        this.loadPackages(); // Recargar la lista
      },
      error: (error) => {
        console.error('Error eliminando paquete:', error);
        alert('Error al eliminar el paquete: ' + (error.error?.Message || error.error?.message || error.message));
        this.stopLoadingSimulation();
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.packageToDelete = '';
  }

  canAddSparePartToPackage(sparePart: any, packageNumber: string): boolean {
    // Verificar que el repuesto no esté ya en un paquete
    if (sparePart.packageNumber && sparePart.packageNumber !== packageNumber) {
      return false;
    }

    // Verificar que el repuesto pertenezca a la misma orden que los repuestos del paquete
    const packageSpareParts = this.selectedPackage?.spareParts || [];
    if (packageSpareParts.length > 0) {
      const firstSparePartOrderId = packageSpareParts[0].orderId;
      return sparePart.orderId === firstSparePartOrderId;
    }

    return true;
  }

  getPackageStatus(packageData: any): string {
    if (packageData.status === 'active') return 'Activo';
    if (packageData.status === 'completed') return 'Completado';
    if (packageData.status === 'cancelled') return 'Cancelado';
    return 'Desconocido';
  }

  getPackageType(packageData: any): string {
    if (packageData.type === 'empaquetado') return 'Empaquetado';
    if (packageData.type === 'cargado') return 'Cargado';
    if (packageData.type === 'enviado') return 'Enviado';
    return 'Desconocido';
  }

  showAddSparePartToPackageModal(): void {
    if (!this.selectedPackage) return;

    this.showAddSparePartModal = true;
    this.loadAvailableSpareParts();
  }

  loadAvailableSpareParts(): void {
    if (!this.selectedPackage) return;

    this.loadingAvailableParts = true;
    // Usar el servicio correcto para obtener repuestos disponibles
    this.packageListsService.getEmpaquetadosParaCargado().subscribe({
      next: (spareParts) => {
        // Filtrar repuestos que no estén ya en un paquete
        this.availableSpareParts = spareParts.filter(sp => !sp.packageNumber);
        this.loadingAvailableParts = false;
      },
      error: (error) => {
        console.error('Error cargando repuestos disponibles:', error);
        alert('Error al cargar repuestos disponibles');
        this.loadingAvailableParts = false;
      }
    });
  }

  addSparePartToPackage(sparePartId: number): void {
    if (!this.selectedPackage) return;

    this.loading = true;
    this.packageListsService.addSparePartToPackage(this.selectedPackage.packageNumber, sparePartId).subscribe({
      next: (response) => {
        alert(response.message);
        this.closeAddSparePartModal();
        this.loadPackages(); // Recargar la lista de paquetes

        // Si el paquete está seleccionado, actualizar sus detalles
        if (this.selectedPackage) {
          this.selectPackage(this.selectedPackage.packageNumber);
        }
      },
      error: (error) => {
        console.error('Error agregando repuesto al paquete:', error);
        alert('Error al agregar repuesto al paquete');
        this.loading = false;
      }
    });
  }

  closeAddSparePartModal(): void {
    this.showAddSparePartModal = false;
    this.availableSpareParts = [];
    this.loadingAvailableParts = false;
  }

  /**
   * Reimprimir solo la etiqueta del paquete (sin las etiquetas individuales)
   */
  /**
   * Reimprimir solo la etiqueta del paquete usando el servicio estándar de labels
   */
  async reprintPackageLabelOnly(packageData: any): Promise<void> {
    if (!packageData) return;

    this.loading = true;

    try {
      // Si no hay items, cargarlos primero
      if (!packageData.items || packageData.items.length === 0) {
        const items = await this.packageListsService.getSparePartsByPackage(packageData.packageNumber).toPromise();
        packageData.items = items;
        packageData.spareParts = items;

        // Actualizar en la lista principal
        const pkgInList = this.packages.find(p => p.packageNumber === packageData.packageNumber);
        if (pkgInList) {
          pkgInList.items = items;
          pkgInList.spareParts = items;
        }
      }

      if (!packageData.items || packageData.items.length === 0) {
        alert('Este paquete no tiene información para reimprimir la etiqueta');
        this.loading = false;
        return;
      }

      // Obtener números de orden únicos del paquete
      const orderNumbers = [...new Set(packageData.items.map((item: any) => item.orderNumber || item.OrderNumber).filter((on: string) => on && on !== 'N/A'))];

      if (orderNumbers.length === 0) {
        if (packageData.orderNumber && packageData.orderNumber !== 'N/A') {
          orderNumbers.push(packageData.orderNumber);
        } else {
          alert('No se encontraron números de orden válidos para generar la etiqueta del paquete');
          this.loading = false;
          return;
        }
      }

      // Usar el primer número de orden
      const mainOrderNumber = orderNumbers[0] as string;

      // Obtener la orden completa
      const orderResponse = await this.ordersService.getAllFilteredOrders(mainOrderNumber).toPromise();
      const order = orderResponse?.find((o: any) => o.orderNumber === mainOrderNumber);

      if (!order) {
        alert('No se pudo encontrar la orden para generar la etiqueta del paquete');
        this.loading = false;
        return;
      }

      if (this.isOrderBlockedForLabelPrint(order)) {
        alert(`No se puede imprimir la etiqueta del paquete ${packageData.packageNumber}: la orden ${mainOrderNumber} tiene ENVIAR = NO.`);
        this.loading = false;
        return;
      }

      // Filtrar repuestos del paquete (usando la misma lógica que el proceso de empaquetado)
      const packageSpareParts = order.spareParts.filter((sp: any) =>
        packageData.items.some((item: any) => item.sparePartId === sp.sparePartId)
      );

      const itemsToPrint = packageSpareParts.length > 0 ? packageSpareParts : packageData.items;

      if (itemsToPrint.length === 0) {
        alert('No se encontraron repuestos válidos en la orden para este paquete');
        this.loading = false;
        return;
      }

      // Crear la etiqueta del paquete EXACTAMENTE igual que en el proceso de empaquetado
      const packageLabel: SparePartLabelInfo = {
        sparePartId: 0, // ID especial para etiqueta del paquete
        sparePartName: 'ETIQUETA DEL PAQUETE',
        productCode: packageData.packageNumber, // Usar el número de paquete como productCode
        packageNumber: packageData.packageNumber,
        orderNumber: mainOrderNumber,
        siniestro: order.siniestro || 'N/A',
        clienteUY: order.clienteUY || 'AUTOCOMERCIAL',
        positionInPackage: 1,
        totalInPackage: itemsToPrint.length
      };

      const labelsForPrint: SparePartLabelInfo[] = [packageLabel];

      // Usar EXACTAMENTE la misma función de impresión que el proceso de empaquetado
      this.labelsService.printLabelsWindow(order, labelsForPrint, true, true);

    } catch (error) {
      console.error('Error al reimprimir etiqueta del paquete:', error);
      alert('Error al reimprimir la etiqueta del paquete');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Reimprimir las etiquetas de un paquete existente
   */
  async reprintPackageLabels(packageData: any): Promise<void> {
    if (!packageData) return;

    // Usar variable local o loading simple, NO startLoadingSimulation
    this.loading = true;

    try {
      // Si no hay items, cargarlos primero
      if (!packageData.items || packageData.items.length === 0) {
        const items = await this.packageListsService.getSparePartsByPackage(packageData.packageNumber).toPromise();
        packageData.items = items;
        packageData.spareParts = items;

        // Actualizar en la lista principal
        const pkgInList = this.packages.find(p => p.packageNumber === packageData.packageNumber);
        if (pkgInList) {
          pkgInList.items = items;
          pkgInList.spareParts = items;
        }
      }

      if (!packageData.items || packageData.items.length === 0) {
        alert('Este paquete no tiene repuestos para reimprimir etiquetas');
        this.loading = false;
        return;
      }

      // Obtener números de orden únicos del paquete
      const orderNumbers = [...new Set(packageData.items.map((item: any) => item.orderNumber || item.OrderNumber).filter((on: string) => on && on !== 'N/A'))];

      if (orderNumbers.length === 0) {
        // Intentar usar el de la cabecera
        if (packageData.orderNumber && packageData.orderNumber !== 'N/A') {
          orderNumbers.push(packageData.orderNumber);
        } else {
          alert('No se encontraron números de orden válidos para generar etiquetas');
          this.loading = false;
          return;
        }
      }


      // Usar el primer número de orden
      const mainOrderNumber = orderNumbers[0] as string;

      // Obtener la orden completa
      const orderResponse = await this.ordersService.getAllFilteredOrders(mainOrderNumber).toPromise();
      const order = orderResponse?.find((o: any) => o.orderNumber === mainOrderNumber);

      if (!order) {
        alert('No se pudo encontrar la orden para generar las etiquetas');
        this.loading = false;
        return;
      }

      if (this.isOrderBlockedForLabelPrint(order)) {
        alert(`No se puede imprimir la etiqueta del paquete ${packageData.packageNumber}: la orden ${mainOrderNumber} tiene ENVIAR = NO.`);
        this.loading = false;
        return;
      }

      // Filtrar solo los repuestos que están en este paquete
      // Asegurarse de comparar strings o numbers consistentemente
      const packageSparePartIds = packageData.items.map((item: any) => item.sparePartId);
      const packageSpareParts = order.spareParts.filter((sp: any) => packageSparePartIds.includes(sp.sparePartId));

      // Si no hay coincidencias directas (raro pero posible si hay discrepancia de datos),
      // usar todos los items del paquete como base si tenemos sus datos mínimos
      const itemsToPrint = packageSpareParts.length > 0 ? packageSpareParts : packageData.items;

      if (itemsToPrint.length === 0) {
        alert('No se encontraron repuestos válidos en la orden para este paquete');
        this.loading = false;
        return;
      }

      // Preparar orden con información de taller (IGUAL QUE package-management.component.ts líneas 3707-3715)
      const orderToUse = {
        ...order,
        workshopId: order.workshopId || null,
        workshopName: order.workshopName || '',
        workshopAddress: order.workshopAddress || '',
        workshopPhone: order.workshopPhone || '',
        workshopCity: order.workshopCity || '',
        workshopDepartment: order.workshopDepartment || ''
      };

      // Crear el packageInfo exactamente como lo hace el proceso de empaquetado
      const packageInfo = {
        packageNumber: packageData.packageNumber,
        orderNumber: mainOrderNumber
      };

      // Usar EXACTAMENTE el mismo método que el proceso de empaquetado
      this.labelsService.printPackageLabelWindow(orderToUse, packageInfo, itemsToPrint.length, itemsToPrint);

      // Marcar como impresa la etiqueta del paquete
      this.packageLabelsPrinted[packageData.packageNumber] = true;

    } catch (error) {
      console.error('Error al reimprimir etiquetas:', error);
      alert('Error al reimprimir las etiquetas del paquete');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Reimprimir las etiquetas individuales de los repuestos del paquete
   */
  async reprintSparePartLabels(packageData: any): Promise<void> {
    if (!packageData) return;

    this.loading = true;

    try {
      // Si no hay items, cargarlos primero
      if (!packageData.items || packageData.items.length === 0) {
        const items = await this.packageListsService.getSparePartsByPackage(packageData.packageNumber).toPromise();
        packageData.items = items;
        packageData.spareParts = items;

        // Actualizar en la lista principal
        const pkgInList = this.packages.find(p => p.packageNumber === packageData.packageNumber);
        if (pkgInList) {
          pkgInList.items = items;
          pkgInList.spareParts = items;
        }
      }

      if (!packageData.items || packageData.items.length === 0) {
        alert('No se encontraron repuestos válidos en el paquete');
        this.loading = false;
        return;
      }

      // Obtener números de orden únicos del paquete
      const orderNumbers = [...new Set(packageData.items.map((item: any) => item.orderNumber || item.OrderNumber).filter((on: string) => on && on !== 'N/A'))];

      if (orderNumbers.length === 0) {
        if (packageData.orderNumber && packageData.orderNumber !== 'N/A') {
          orderNumbers.push(packageData.orderNumber);
        } else {
          alert('No se encontraron números de orden válidos para generar las etiquetas');
          this.loading = false;
          return;
        }
      }

      // Usar el primer número de orden
      const mainOrderNumber = orderNumbers[0] as string;

      // Obtener la orden completa
      const orderResponse = await this.ordersService.getAllFilteredOrders(mainOrderNumber).toPromise();
      const order = orderResponse?.find((o: any) => o.orderNumber === mainOrderNumber);

      if (!order) {
        alert('No se pudo encontrar la orden para generar las etiquetas');
        this.loading = false;
        return;
      }

      // Filtrar repuestos del paquete que coincidan con los de la orden
      const packageSpareParts = order.spareParts.filter((sp: any) =>
        packageData.items.some((item: any) => item.sparePartId === sp.sparePartId)
      );

      const itemsToPrint = packageSpareParts.length > 0 ? packageSpareParts : packageData.items;

      if (itemsToPrint.length === 0) {
        alert('No se encontraron repuestos válidos en la orden para este paquete');
        this.loading = false;
        return;
      }

      // Crear etiquetas individuales para cada repuesto usando el mismo formato que el resto del sistema
      const labelsForPrint: SparePartLabelInfo[] = itemsToPrint.map((sparePart: any, index: number) => ({
        sparePartId: sparePart.sparePartId,
        sparePartName: sparePart.sparePartName,
        productCode: sparePart.productCode,
        packageNumber: packageData.packageNumber,
        orderNumber: mainOrderNumber,
        siniestro: order.siniestro || 'N/A',
        clienteUY: order.clienteUY || 'AUTOCOMERCIAL',
        positionInPackage: index + 1,
        totalInPackage: itemsToPrint.length
      }));

      // Usar el servicio con control de PIN por repuesto (sin etiqueta de paquete)
      this.labelPrintAuth.printAuthorizedSparePartLabels(order, labelsForPrint, {
        useSharedNumber: false,
        includePackageLabel: false
      }).subscribe({
        next: () => {
          this.sparePartLabelsPrinted[packageData.packageNumber] = true;
        },
        error: (error) => {
          alert(error?.message || 'No se pudo completar la impresión.');
        }
      });

    } catch (error) {
      console.error('Error al reimprimir etiquetas de repuestos:', error);
      alert('Error al reimprimir las etiquetas de los repuestos');
    } finally {
      this.loading = false;
    }
  }
}

